import { useState, useEffect } from "react";
import Sidebar from "../components/layout/Sidebar";
import Header from "../components/layout/Header";
import Dialog from "../components/dashboard/Dialog";
import { useSession } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import imageCompression from 'browser-image-compression';
import api from "../services/api";

export default function AdminUsers() {
  const { status } = useSession();
  const navigate = useNavigate();
  
  const [staff, setStaff] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [editTarget, setEditTarget] = useState<any>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Form State
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    role: "COACH",
    password: ""
  });
  const [avatar, setAvatar] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);

  const generateRandomPassword = () => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%&*";
    return Array.from({length: 12}, () => chars[Math.floor(Math.random() * chars.length)]).join('');
  };

  const fetchStaff = async () => {
    try {
      const res = await api.get('/admin/staff');
      setStaff(res.data);
    } catch (err) {
      console.error("Failed to fetch staff:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (status === "unauthenticated") {
       navigate("/login");
    } else if (status === "authenticated") {
       fetchStaff();
    }
  }, [status, navigate]);

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Show preview
    const reader = new FileReader();
    reader.onloadend = () => setAvatarPreview(reader.result as string);
    reader.readAsDataURL(file);

    // Compress image
    try {
      const options = { maxSizeMB: 0.5, maxWidthOrHeight: 800, useWebWorker: true };
      const compressedFile = await imageCompression(file, options);
      setAvatar(compressedFile);
    } catch (err) {
      console.error("Image compression failed:", err);
      setAvatar(file);
    }
  };

  const handleOnboard = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const data = new FormData();
      data.append("name", formData.name);
      data.append("email", formData.email);
      data.append("role", formData.role);
      data.append("password", formData.password);
      if (avatar) data.append("avatar", avatar);

      await api.post('/admin/staff', data);
      setIsModalOpen(false);
      setFormData({ name: "", email: "", role: "COACH", password: "" });
      setAvatar(null);
      setAvatarPreview(null);
      fetchStaff();
    } catch (err: any) {
      console.error("Onboarding failed", err);
      alert(err.response?.data?.error || "Onboarding failed");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (member: any) => {
    setEditTarget(member);
    setFormData({
      name: member.name || "",
      email: member.email || "",
      role: member.role || "COACH",
      password: "" // Not used for edit
    });
    setAvatarPreview(member.avatarUrl);
    setAvatar(null);
    setIsEditModalOpen(true);
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editTarget) return;
    setIsSubmitting(true);
    try {
      const data = new FormData();
      data.append("name", formData.name);
      if (avatar) data.append("avatar", avatar);

      await api.patch(`/admin/staff/${editTarget.id}`, data);
      setIsEditModalOpen(false);
      setEditTarget(null);
      setAvatar(null);
      setAvatarPreview(null);
      fetchStaff();
    } catch (err: any) {
      console.error("Update failed", err);
      alert(err.response?.data?.error || "Update failed");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = (id: string) => {
    setDeleteTargetId(id);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteTargetId) return;
    setIsSubmitting(true);
    try {
      await api.delete(`/admin/staff/${deleteTargetId}`);
      setIsDeleteModalOpen(false);
      setDeleteTargetId(null);
      fetchStaff();
    } catch (err) {
      console.error("Deletion failed", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredStaff = staff.filter(s => 
    s.name?.toLowerCase().includes(searchQuery.toLowerCase()) || 
    s.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getRoleColor = (role: string) => {
    switch (role) {
      case "ADMIN": return "bg-purple-500/10 text-purple-500 border-purple-500/20";
      case "COACH": return "bg-emerald-500/10 text-emerald-500 border-emerald-500/20";
      case "MARKETING_ASSOCIATE": return "bg-blue-500/10 text-blue-500 border-blue-500/20";
      case "COACHING_ASSOCIATE": return "bg-amber-500/10 text-amber-500 border-amber-500/20";
      default: return "bg-slate-500/10 text-slate-500 border-slate-500/20";
    }
  };

  const getRoleName = (role: string) => {
    return role.replace("_", " ");
  };

  return (
    <div className="min-h-screen bg-background text-on-background">
      <Header />
      <Sidebar />
      
      <main className="ml-0 md:ml-20 pt-20 pb-12 px-4 md:px-8">
        <div className="max-w-7xl mx-auto space-y-8 sm:space-y-12">
          {/* Header & Stats Banner */}
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-end gap-6">
            <div className="space-y-2">
              <span className="text-[10px] font-black uppercase tracking-[0.2em] text-primary">Operational Hub</span>
              <h1 className="text-3xl sm:text-5xl font-black text-on-surface tracking-tighter leading-tight">
                Staff <span className="text-outline-variant/40">Directory.</span>
              </h1>
            </div>
            <button 
              onClick={() => {
                setFormData(prev => ({ ...prev, password: generateRandomPassword() }));
                setIsModalOpen(true);
              }}
              className="w-full sm:w-auto px-6 py-4 bg-primary text-surface rounded-[1.5rem] font-black text-xs uppercase tracking-widest hover:translate-y-[-2px] hover:shadow-2xl transition-all active:scale-95 shadow-xl shadow-primary/20 flex items-center justify-center gap-2"
            >
              <span className="material-symbols-outlined text-sm">person_add</span>
              Onboard Member
            </button>
          </div>

          {/* Search & Utility Bar */}
          <div className="relative group">
            <span className="material-symbols-outlined absolute left-6 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-primary transition-colors">search</span>
            <input 
              type="text" 
              placeholder="Search by name or email address..."
              className="w-full pl-16 pr-6 py-4 sm:py-5 bg-surface-container-low border border-outline-variant/10 rounded-[1.5rem] sm:rounded-[2rem] outline-none focus:ring-4 focus:ring-primary/5 focus:border-primary/20 transition-all text-sm font-medium shadow-sm text-on-surface"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {/* Directory Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 animate-in fade-in slide-in-from-bottom-4 duration-700">
            {loading ? (
              [1, 2, 3].map(i => (
                <div key={i} className="h-56 bg-surface-container-low rounded-[1.75rem] sm:rounded-[2.5rem] animate-pulse" />
              ))
            ) : filteredStaff.length > 0 ? filteredStaff.map((member) => (
              <div key={member.id} className="bg-surface-container-low p-6 sm:p-8 rounded-[1.75rem] sm:rounded-[2.5rem] border border-outline-variant/10 shadow-sm hover:shadow-xl transition-all group relative overflow-hidden flex flex-col justify-between min-h-[240px]">
                <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full -translate-y-1/2 translate-x-1/2 blur-2xl group-hover:bg-primary/10 transition-colors" />
                
                <div>
                  <div className="flex justify-between items-start mb-6 relative">
                    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center font-black text-lg overflow-hidden border ${getRoleColor(member.role)}`}>
                      {member.avatarUrl ? (
                        <img src={member.avatarUrl} alt={member.name} className="w-full h-full object-cover" />
                      ) : (
                        member.name?.[0] || "U"
                      )}
                    </div>
                    <div className={`px-3 py-1 sm:px-4 sm:py-1.5 rounded-full text-[9px] font-black uppercase tracking-widest border ${getRoleColor(member.role)}`}>
                      {getRoleName(member.role)}
                    </div>
                  </div>

                  <div className="space-y-1 mb-6">
                    <h3 className="text-lg sm:text-xl font-black text-on-surface tracking-tight truncate">{member.name || "Unnamed User"}</h3>
                    <p className="text-xs font-medium text-slate-500 dark:text-slate-400 truncate">{member.email}</p>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-6 border-t border-outline-variant/10 gap-2">
                   <div className="flex flex-col min-w-0">
                      <span className="text-[9px] font-black uppercase tracking-widest text-slate-400">Security Clearance</span>
                      <span className="text-[10px] font-bold text-slate-600 dark:text-slate-300 truncate">Level 4 • Standard</span>
                   </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button 
                        onClick={() => handleEdit(member)}
                        className="w-10 h-10 rounded-xl bg-primary/5 text-primary hover:text-primary hover:bg-primary/10 transition-all flex items-center justify-center"
                        title="Edit Personnel"
                      >
                         <span className="material-symbols-outlined text-sm">edit</span>
                      </button>
                      <button 
                        onClick={() => handleDelete(member.id)}
                        className="w-10 h-10 rounded-xl bg-red-500/5 text-red-500 hover:text-red-500 hover:bg-red-500/10 transition-all flex items-center justify-center"
                        title="Revoke Access"
                      >
                         <span className="material-symbols-outlined text-sm">delete</span>
                      </button>
                   </div>
                </div>
              </div>
            )) : (
              <div className="col-span-full py-20 text-center space-y-4 opacity-30">
                 <span className="material-symbols-outlined text-6xl">manage_accounts</span>
                 <p className="text-xl font-black tracking-tight">No tactical personnel found matching your search.</p>
              </div>
            )}
          </div>

      {/* Onboard Dialog */}
      <Dialog isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title="Onboard Personnel">
        <form onSubmit={handleOnboard} className="space-y-6 pt-2">
          
          {/* Avatar Upload Section */}
          <div className="flex flex-col items-center gap-3 py-2">
            <div className="relative group cursor-pointer">
              <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-[1.5rem] sm:rounded-[2rem] bg-surface-container-high border-2 border-dashed border-outline-variant/20 flex items-center justify-center overflow-hidden group-hover:border-primary/40 transition-all">
                {avatarPreview ? (
                  <img src={avatarPreview} alt="Preview" className="w-full h-full object-cover" />
                ) : (
                  <span className="material-symbols-outlined text-slate-400 text-2xl sm:text-3xl">add_a_photo</span>
                )}
              </div>
              <input 
                type="file" 
                accept="image/*"
                onChange={handleAvatarChange}
                className="absolute inset-0 opacity-0 cursor-pointer"
              />
              <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-primary text-surface rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                <span className="material-symbols-outlined text-sm">upload</span>
              </div>
            </div>
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Tactical Profile Picture</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 px-1">Full Name</label>
              <input 
                required
                type="text" 
                placeholder="e.g. John Wick"
                className="w-full px-5 py-3.5 bg-surface-container-high text-on-surface border border-outline-variant/10 rounded-xl focus:ring-2 focus:ring-primary/20 outline-none transition-all text-sm font-medium"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 px-1">Email Address</label>
              <input 
                required
                type="email" 
                placeholder="john@hitclicks.com"
                className="w-full px-5 py-3.5 bg-surface-container-high text-on-surface border border-outline-variant/10 rounded-xl focus:ring-2 focus:ring-primary/20 outline-none transition-all text-sm font-medium"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 px-1">Tactical Role</label>
            <select 
              className="w-full px-5 py-3.5 bg-surface-container-high text-on-surface border border-outline-variant/10 rounded-xl focus:ring-2 focus:ring-primary/20 outline-none transition-all text-sm font-black appearance-none cursor-pointer"
              value={formData.role}
              onChange={(e) => setFormData({...formData, role: e.target.value})}
            >
              <option value="COACH">Execution Coach</option>
              <option value="MARKETING_ASSOCIATE">Marketing Associate</option>
              <option value="COACHING_ASSOCIATE">Coaching Associate</option>
              <option value="ADMIN">System Administrator</option>
            </select>
          </div>

          <div className="space-y-2">
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 px-1">Access Password</label>
            <div className="relative">
              <input 
                required
                type="text" 
                placeholder="Min 8 characters"
                className="w-full px-5 py-3.5 bg-surface-container-high text-on-surface border border-outline-variant/10 rounded-xl focus:ring-2 focus:ring-primary/20 outline-none transition-all text-sm font-medium pr-16"
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
              />
              <button 
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, password: generateRandomPassword() }))}
                className="absolute right-4 top-1/2 -translate-y-1/2 p-2 text-primary hover:bg-primary/5 rounded-lg transition-all"
              >
                <span className="material-symbols-outlined text-sm">refresh</span>
              </button>
            </div>
          </div>

          <div className="pt-4">
            <button 
              disabled={isSubmitting}
              className="w-full py-4 sm:py-5 bg-primary text-surface rounded-[1.25rem] sm:rounded-[1.5rem] font-black text-xs uppercase tracking-widest hover:translate-y-[-2px] hover:shadow-2xl transition-all shadow-xl shadow-primary/20 disabled:opacity-50"
            >
              {isSubmitting ? "Generating Credentials..." : "Finalize Onboarding"}
            </button>
          </div>
        </form>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} title="Update Personnel Details">
        <form onSubmit={handleUpdate} className="space-y-6 pt-2">
          
          <div className="flex flex-col items-center gap-3 py-2">
            <div className="relative group cursor-pointer">
              <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-[1.5rem] sm:rounded-[2rem] bg-surface-container-high border-2 border-dashed border-outline-variant/20 flex items-center justify-center overflow-hidden group-hover:border-primary/40 transition-all">
                {avatarPreview ? (
                  <img src={avatarPreview} alt="Preview" className="w-full h-full object-cover" />
                ) : (
                  <span className="material-symbols-outlined text-slate-400 text-2xl sm:text-3xl">add_a_photo</span>
                )}
              </div>
              <input 
                type="file" 
                accept="image/*"
                onChange={handleAvatarChange}
                className="absolute inset-0 opacity-0 cursor-pointer"
              />
              <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-primary text-surface rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                <span className="material-symbols-outlined text-sm">edit</span>
              </div>
            </div>
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Update Profile Visual</p>
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 px-1">Tactical Codename (Full Name)</label>
              <input 
                required
                type="text" 
                className="w-full px-5 py-3.5 bg-surface-container-high text-on-surface border border-outline-variant/10 rounded-xl focus:ring-4 focus:ring-primary/5 focus:border-primary/20 outline-none transition-all text-sm font-bold shadow-sm"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
              />
            </div>

            <div className="p-4 bg-surface-container-high/50 rounded-xl border border-outline-variant/10 opacity-70">
              <div className="flex items-center gap-3">
                 <span className="material-symbols-outlined text-sm text-slate-400">lock</span>
                 <div>
                    <p className="text-[9px] font-black uppercase tracking-widest text-slate-500">Immutable Credentials</p>
                    <p className="text-xs font-bold text-slate-600 dark:text-slate-300">{formData.email}</p>
                 </div>
              </div>
            </div>
          </div>

          <div className="pt-4">
            <button 
              disabled={isSubmitting}
              className="w-full py-4 sm:py-5 bg-primary text-surface rounded-[1.25rem] sm:rounded-[1.5rem] font-black text-xs uppercase tracking-widest hover:translate-y-[-2px] hover:shadow-2xl transition-all shadow-xl shadow-primary/20 disabled:opacity-50 active:scale-[0.98]"
            >
              {isSubmitting ? "Syncing Intelligence..." : "Update Personnel Record"}
            </button>
          </div>
        </form>
      </Dialog>

      <Dialog 
        isOpen={isDeleteModalOpen} 
        onClose={() => setIsDeleteModalOpen(false)} 
        title="Revoke Access?"
        className="!max-w-md border-red-500/20"
      >
        <div className="space-y-6 text-center py-2">
          <div className="w-16 h-16 sm:w-20 sm:h-20 bg-red-500/10 text-red-500 rounded-2xl sm:rounded-3xl flex items-center justify-center mx-auto mb-4">
            <span className="material-symbols-outlined text-3xl sm:text-4xl">person_remove</span>
          </div>
          <div className="space-y-2">
            <h4 className="text-lg sm:text-xl font-black text-on-surface tracking-tight">Personnel Liquidation</h4>
            <p className="text-xs sm:text-sm font-medium text-slate-500 dark:text-slate-400 leading-relaxed px-2">
              You are about to revoke all security clearances for this operative. This action is <strong className="text-red-500 font-bold">permanent</strong> and will result in immediate system expulsion.
            </p>
          </div>
          <div className="flex gap-3 sm:gap-4 pt-4">
            <button 
              onClick={() => setIsDeleteModalOpen(false)}
              className="flex-1 py-3.5 bg-surface-container-high text-on-surface-variant rounded-xl sm:rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-surface-container-highest transition-all"
            >
              Abeyance
            </button>
            <button 
              onClick={confirmDelete}
              disabled={isSubmitting}
              className="flex-1 py-3.5 bg-red-500 text-white rounded-xl sm:rounded-2xl font-black text-xs uppercase tracking-widest hover:translate-y-[-2px] hover:shadow-xl hover:shadow-red-500/20 transition-all active:scale-95 flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : "Revoke Access"}
            </button>
          </div>
        </div>
      </Dialog>
        </div>
      </main>
    </div>
  );
}
